import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FeatureFlags } from '@feature-flags/feature-flags.const';
import { ContextService } from 'app/core/services/context.service';
import { ApiResponse, Client, Site, SiteScacCodes } from 'app/shared/types';
import { environment } from 'environment/environment';
import { Observable } from 'rxjs';
import { BaseApiService } from './base-api.service';

@Injectable({
  providedIn: 'root',
})
export class ClientsService extends BaseApiService<
  ApiResponse<{ clients: Client[] }>,
  ApiResponse<{ client: Client }>,
  ApiResponse<{ id: string }>
> {
  private contextService = inject(ContextService);

  protected readonly carrierManagementImprovement = this.contextService.isFeatureFlagEnabled(
    FeatureFlags.CarrierManagementImprovement,
  );

  constructor(public override http: HttpClient) {
    super(http, environment.siteManagementApiBaseUrl);
  }

  public override controllerName(): string {
    return `api/clients`;
  }

  public override getById(id: string): Observable<ApiResponse<{ client: Client }>> {
    if (this.carrierManagementImprovement) {
      const params = new HttpParams().set('api-version', '2.0');
      return this.http.get<ApiResponse<{ client: Client }>>(`${this.api}/${this.controllerName()}/${id}`, { params });
    }

    return super.getById(id);
  }

  public override insert(client: Client): Observable<ApiResponse<{ id: string }>> {
    if (!this.carrierManagementImprovement) {
      return super.insert(client);
    }

    const params = new HttpParams().set('api-version', '2.0');
    return this.http.post<ApiResponse<{ id: string }>>(`${this.api}/${this.controllerName()}`, client, { params });
  }

  public override update(id: string, client: Client): Observable<ApiResponse<{ id: string }>> {
    if (this.carrierManagementImprovement) {
      const params = new HttpParams().set('api-version', '2.0');
      return this.http.put<ApiResponse<{ id: string }>>(`${this.api}/${this.controllerName()}/${id}`, client, {
        params,
      });
    }

    return super.update(id, client);
  }

  public getSitesByClient(clientId: string | null): Observable<ApiResponse<{ sites: Site[] }>> {
    return this.http.get<ApiResponse<{ sites: Site[] }>>(`${this.api}/${this.controllerName()}/${clientId}/sites`);
  }

  public getScacCodes(id: string): Observable<ApiResponse<{ scacs: SiteScacCodes[] }>> {
    return this.http.get<ApiResponse<{ scacs: SiteScacCodes[] }>>(`${this.api}/${this.controllerName()}/${id}/scacs`);
  }
}
